import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Leaf, Plus, Download, Trash2, Search, Filter, 
    CheckSquare, Square, X, DownloadCloud, AlertCircle,
    Calendar, MapPin, CheckCircle, LayoutGrid, List
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { nativeDownload } from '../utils/nativeDownload';
import SignatureManager from '../components/profile/SignatureManager';

export default function HerbariumManager({ plants = [] }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [hasSignature, setHasSignature] = useState(false);
    const [signatureData, setSignatureData] = useState(null);
    const [showBulkSelector, setShowBulkSelector] = useState(false);
    const [bulkSelectedPlants, setBulkSelectedPlants] = useState([]);
    const [isCreatingBulk, setIsCreatingBulk] = useState(false);
    const [viewMode, setViewMode] = useState('grid');
    
    const { fetchWithAuth } = useContext(AuthContext);
    const { showToast: addToast } = useToast();
    const navigate = useNavigate();

    useEffect(() => {
        fetchEntries();
        fetchProfile();
    }, []);

    const fetchEntries = async () => {
        setLoading(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/herbarium/');
            if (response.ok) {
                const data = await response.json();
                setEntries(data);
            }
        } catch (error) {
            console.error('Error fetching herbarium:', error);
        } finally {
            setLoading(false);
        }
    };

    const [profileLoading, setProfileLoading] = useState(false);
    
    const fetchProfile = async () => {
        setProfileLoading(true);
        try {
            const response = await fetchWithAuth('/api/profile/');
            if (response.ok) {
                const data = await response.json();
                setHasSignature(data.has_signature);
                setSignatureData(data.signature_data);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setProfileLoading(false);
        }
    };

    const handleBatchDelete = async () => {
        if (!window.confirm(`Möchtest du wirklich ${selectedIds.length} Einträge löschen?`)) return;
        
        try {
            const response = await fetchWithAuth('/api/apprentice/herbarium/batch-delete/', {
                method: 'POST',
                body: JSON.stringify({ ids: selectedIds })
            });
            if (response.ok) {
                addToast('Einträge wurden gelöscht', 'success');
                setSelectedIds([]);
                fetchEntries();
            }
        } catch (error) {
            addToast('Fehler beim Löschen', 'error');
        }
    };

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleBatchDownload = async (format = 'zip') => {
        try {
            addToast(`Download (${format.toUpperCase()}) wird vorbereitet...`, 'info');
            const response = await fetchWithAuth('/api/apprentice/herbarium/batch-download/', {
                method: 'POST',
                body: JSON.stringify({ 
                    ids: selectedIds,
                    format: format
                })
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const filename = format === 'pdf' ? 'Herbarium_Export.pdf' : 'Herbarium_Export.zip';
                downloadBlob(blob, filename);
                addToast('Download erfolgreich gestartet', 'success');
            } else {
                addToast('Download fehlgeschlagen', 'error');
            }
        } catch (error) {
            addToast('Fehler beim Download', 'error');
        }
    };

    const handleSinglePdfDownload = async (entryId, plantName) => {
        try {
            addToast('PDF wird generiert...', 'info');
            const response = await fetchWithAuth(`/api/apprentice/herbarium/${entryId}/download_pdf/`);
            if (response.ok) {
                const blob = await response.blob();
                downloadBlob(blob, `Herbarium_${plantName || entryId}.pdf`);
                addToast('PDF heruntergeladen', 'success');
            } else {
                addToast('PDF konnte nicht erstellt werden', 'error');
            }
        } catch (error) {
            addToast('Fehler beim PDF-Download', 'error');
        }
    };

    const [bulkSearchQuery, setBulkSearchQuery] = useState('');
    
    const handleBulkCreate = async () => {
        if (bulkSelectedPlants.length === 0) return;
        setIsCreatingBulk(true);
        try {
            const response = await fetchWithAuth('/api/apprentice/herbarium/bulk-create/', {
                method: 'POST',
                body: JSON.stringify({ plant_ids: bulkSelectedPlants })
            });
            if (response.ok) {
                const data = await response.json();
                addToast(data.message || 'Einträge wurden erstellt', 'success');
                setShowBulkSelector(false);
                setBulkSelectedPlants([]);
                fetchEntries();
            }
        } catch (error) {
            addToast('Fehler bei der Massenerstellung', 'error');
        } finally {
            setIsCreatingBulk(false);
        }
    };

    const filteredBulkPlants = (plants || []).filter(p => {
        const q = bulkSearchQuery.toLowerCase();
        return (p.botanical_name || '').toLowerCase().includes(q) || 
               (p.german_name || '').toLowerCase().includes(q);
    });

    const handleSelectAllBulk = () => {
        const visibleIds = filteredBulkPlants.map(p => p.id);
        const allSelected = visibleIds.every(id => bulkSelectedPlants.includes(id));
        
        if (allSelected) {
            // Unselect all VISIBLE ones
            setBulkSelectedPlants(prev => prev.filter(id => !visibleIds.includes(id)));
        } else {
            // Select all VISIBLE ones (avoiding duplicates)
            setBulkSelectedPlants(prev => [...new Set([...prev, ...visibleIds])]);
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === entries.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(entries.map(e => e.id));
        }
    };

    const filteredEntries = entries.filter(e => {
        const query = searchQuery.toLowerCase();
        const botanical = (e.plant_name || '').toLowerCase();
        const german = (e.plant_german_name || '').toLowerCase();
        const loc = (e.location || '').toLowerCase();
        return botanical.includes(query) || german.includes(query) || loc.includes(query);
    });

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#1a1a1a] py-8 px-4 pb-24">
            <div className="max-w-7xl mx-auto relative">
                {/* Header */}
                <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div className="animate-in slide-in-from-left duration-700">
                        <div className="flex items-center gap-4 mb-3">
                            <div className="p-3 bg-white dark:bg-gray-800 rounded-2xl shadow-premium border border-gray-100 dark:border-gray-800">
                                <Leaf className="w-8 h-8 text-plantlix-500 dark:text-plantlix-400" />
                            </div>
                            <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight">
                                Herbarium
                            </h1>
                        </div>
                        <p className="text-lg text-gray-500 dark:text-gray-400 font-medium">
                            Deine digitale Herbar-Sammlung <span className="text-plantlix-500 opacity-50 px-1">•</span> <span className="italic">Exklusive Edition</span>
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-white dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-800 rounded-2xl p-1.5 shadow-sm">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-xl transition-all ${viewMode === 'grid' ? 'bg-plantlix-500 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <LayoutGrid className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-xl transition-all ${viewMode === 'list' ? 'bg-plantlix-500 text-white shadow-lg' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <List className="w-5 h-5" />
                            </button>
                        </div>

                        <button
                            onClick={() => setShowBulkSelector(true)}
                            className="hidden sm:flex items-center gap-2 px-6 py-3 bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-200 rounded-2xl font-bold text-sm transition-all shadow-sm hover:shadow-md"
                        >
                            <Plus className="w-5 h-5 text-plantlix-500" />
                            <span>Massen-Erstellung</span>
                        </button>
                        <button
                            onClick={() => navigate('/apprentice/herbarium/new')}
                            className="flex items-center gap-2 px-6 py-3 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-2xl font-bold text-sm transition-all shadow-xl hover:scale-105"
                        >
                            <Leaf className="w-5 h-5" />
                            <span>Neues Blatt</span>
                        </button>
                    </div>
                </div>

            {/* Top Grid: Info & Signature */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-gradient-to-r from-plantlix-500/10 to-plantlix-500/10 dark:from-plantlix-900/20 dark:to-plantlix-700/20 rounded-2xl p-6 border border-plantlix-500/20 flex items-center gap-6">
                    <div className="p-4 bg-white dark:bg-gray-800 rounded-2xl shadow-premium text-plantlix-500">
                        <DownloadCloud className="w-8 h-8" />
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-gray-900 dark:text-white mb-1">Batch-Funktionen</h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                            Wähle mehrere Blätter aus für den gesammelten PDF-Export.
                        </p>
                    </div>
                </div>

                <div className="lg:col-span-1">
                    <SignatureManager 
                        hasSignature={hasSignature} 
                        signatureData={signatureData}
                        onUpdate={fetchProfile} 
                        loading={profileLoading}
                    />
                </div>
            </div>

            {/* List Control */}
            {/* Controls */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex gap-4 items-center flex-1">
                    <div className="relative flex-1 md:max-w-md">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input 
                            type="text"
                            placeholder="Herbarium durchsuchen..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-plantlix-500 transition-all shadow-sm"
                        />
                    </div>
                    {entries.length > 0 && (
                        <button 
                            onClick={toggleSelectAll}
                            className="p-3 bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 rounded-2xl text-gray-500 hover:text-plantlix-500 transition-colors shadow-sm"
                            title="Alle auswählen"
                        >
                            {selectedIds.length === entries.length ? <CheckSquare className="w-5 h-5 text-plantlix-500" /> : <Square className="w-5 h-5" />}
                        </button>
                    )}
                </div>

                {selectedIds.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3 animate-in zoom-in duration-300">
                        <button 
                            onClick={() => handleBatchDownload('pdf')}
                            className="flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-black text-white rounded-2xl font-bold text-sm shadow-xl"
                        >
                            <Download className="w-4 h-4" />
                            <span>PDF ({selectedIds.length})</span>
                        </button>
                        <button 
                            onClick={() => handleBatchDownload('zip')}
                            className="flex items-center gap-2 px-6 py-3 bg-plantlix-500 text-white rounded-2xl font-bold text-sm shadow-xl"
                        >
                            <Download className="w-4 h-4" />
                            <span>ZIP ({selectedIds.length})</span>
                        </button>
                        <button 
                            onClick={handleBatchDelete}
                            className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-2xl transition-all"
                        >
                            <Trash2 className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Content Area */}
            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4 bg-white dark:bg-[#1a1a1a] rounded-3xl border border-gray-100 dark:border-gray-800">
                    <div className="w-12 h-12 border-4 border-plantlix-500/20 border-t-plantlix-500 rounded-full animate-spin"></div>
                    <p className="text-gray-500 font-black uppercase tracking-widest text-xs">Synchronisiere Sammlung...</p>
                </div>
            ) : filteredEntries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-center px-4 bg-white dark:bg-[#1a1a1a] rounded-3xl border border-gray-100 dark:border-gray-800 animate-in fade-in">
                    <div className="w-24 h-24 bg-gray-50 dark:bg-gray-800/50 rounded-full flex items-center justify-center mb-6">
                        <Leaf className="w-12 h-12 text-gray-300" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-2 uppercase tracking-tight">Leeres Herbarium</h3>
                    <p className="text-gray-500 dark:text-gray-400 max-w-sm font-medium">
                        Deine digitale Sammlung wartet auf ihr erstes Fundstück.
                    </p>
                </div>
            ) : (
                <div className={viewMode === 'grid' ? 'grid gap-6 md:grid-cols-2 lg:grid-cols-3' : 'space-y-4'}>
                    {filteredEntries.map(entry => (
                        <div 
                            key={entry.id} 
                            onClick={() => toggleSelect(entry.id)}
                            className={`group relative overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl border-2 transition-all duration-300 transform hover:-translate-y-1 shadow-sm hover:shadow-xl cursor-pointer ${
                                selectedIds.includes(entry.id) 
                                ? 'border-plantlix-500 ring-4 ring-plantlix-500/10' 
                                : 'border-gray-100 dark:border-gray-800'
                            }`}
                        >
                            {/* Selection Checkbox */}
                            <div className={`absolute top-4 left-4 z-10 p-1.5 rounded-xl transition-all ${
                                selectedIds.includes(entry.id) 
                                ? 'bg-plantlix-500 text-white shadow-lg' 
                                : 'bg-white/90 dark:bg-black/40 text-gray-400 opacity-0 group-hover:opacity-100'
                            }`}>
                                {selectedIds.includes(entry.id) ? <CheckCircle className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                            </div>

                            <div className="p-6">
                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-800 shadow-sm">
                                        {entry.image ? (
                                            <img src={entry.image} className="w-full h-full object-cover" alt="" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                                                <Leaf className="w-8 h-8" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        {entry.has_signature ? (
                                            <div className="px-3 py-1 bg-plantlix-50 dark:bg-plantlix-900/20 text-plantlix-500 border border-plantlix-100 dark:border-plantlix-800 rounded-full text-[10px] font-black uppercase tracking-tighter flex items-center gap-1.5">
                                                <CheckCircle className="w-3.5 h-3.5" />
                                                Signiert
                                            </div>
                                        ) : (
                                            <div className="px-3 py-1 bg-amber-50 dark:bg-amber-900/20 text-amber-600 border border-amber-100 dark:border-amber-800 rounded-full text-[10px] font-black uppercase tracking-tighter flex items-center gap-1.5">
                                                <AlertCircle className="w-3.5 h-3.5" />
                                                Entwurf
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-1 line-clamp-1 leading-tight">
                                        {entry.form_data?.botanical_name || entry.plant_name || 'Unbenannt'}
                                    </h3>
                                    <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest italic">
                                        {entry.form_data?.german_name || entry.plant_german_name || '-'}
                                    </p>
                                </div>

                                <div className="flex flex-wrap gap-4 mb-8 pt-6 border-t border-gray-50 dark:border-gray-800">
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-black text-gray-300 dark:text-gray-600 uppercase tracking-widest">Gefunden am</span>
                                        <div className="flex items-center gap-1.5 mt-0.5 font-bold text-sm text-gray-700 dark:text-gray-300">
                                            <Calendar className="w-3.5 h-3.5 text-blue-500" />
                                            {new Date(entry.captured_at).toLocaleDateString('de-DE')}
                                        </div>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[9px] font-black text-gray-300 dark:text-gray-600 uppercase tracking-widest">Fundort</span>
                                        <div className="flex items-center gap-1.5 mt-0.5 font-bold text-sm text-gray-700 dark:text-gray-300">
                                            <MapPin className="w-3.5 h-3.5 text-red-500" />
                                            {entry.location || 'Unbekannt'}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-2">
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); navigate(`/apprentice/herbarium/${entry.id}`); }}
                                        className="flex-1 px-6 py-3 bg-gray-900 dark:bg-black text-white rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-plantlix-500 transition-all"
                                    >
                                        Editieren
                                    </button>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleSinglePdfDownload(entry.id, entry.plant_name); }}
                                        className="p-3 bg-plantlix-500/10 text-plantlix-500 hover:bg-plantlix-500 hover:text-white rounded-2xl transition-all shadow-sm"
                                        title="Export PDF"
                                    >
                                        <Download className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Bulk Selector Modal */}
            {showBulkSelector && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl overflow-hidden">
                        <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Massen-Erzeugung</h2>
                                <p className="text-sm text-gray-500">Wähle Pflanzen aus dem Bestiarium aus.</p>
                            </div>
                            <button onClick={() => setShowBulkSelector(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-all">
                                <X className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>
                        
                        <div className="p-4 bg-gray-50 dark:bg-gray-800/20 flex items-center gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input 
                                    type="text"
                                    placeholder="Nach Pflanzen suchen..."
                                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-plantlix-500"
                                    value={bulkSearchQuery}
                                    onChange={(e) => setBulkSearchQuery(e.target.value)}
                                />
                            </div>
                            <button 
                                onClick={handleSelectAllBulk}
                                className="px-4 py-2 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 transition-all whitespace-nowrap flex items-center gap-2"
                            >
                                <CheckSquare className="w-4 h-4 text-plantlix-500" />
                                <span>Alle (Sichtbar)</span>
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto p-4">
                             <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {filteredBulkPlants.length === 0 ? (
                                    <div className="col-span-full py-10 text-center text-gray-500">
                                        Keine Pflanzen für "{bulkSearchQuery}" gefunden.
                                    </div>
                                ) : filteredBulkPlants.map(plant => (
                                    <div 
                                        key={plant.id} 
                                        onClick={() => {
                                            setBulkSelectedPlants(prev => 
                                                prev.includes(plant.id) ? prev.filter(id => id !== plant.id) : [...prev, plant.id]
                                            );
                                        }}
                                        className={`flex items-center gap-3 p-4 rounded-2xl cursor-pointer transition-all border-2 ${
                                            bulkSelectedPlants.includes(plant.id) 
                                            ? 'bg-plantlix-50 dark:bg-plantlix-900/20 border-plantlix-500' 
                                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/40 border-gray-100 dark:border-white/5'
                                        }`}
                                    >
                                        <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800 flex-shrink-0 border border-gray-100 dark:border-white/10">
                                            {plant.image_url ? (
                                                <img src={plant.image_url} className="w-full h-full object-cover" alt="" />
                                            ) : <Leaf className="w-full h-full p-3 text-gray-300" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-bold text-sm truncate dark:text-white capitalize">{plant.botanical_name}</div>
                                            <div className="text-xs text-gray-500 truncate">{plant.german_name}</div>
                                        </div>
                                        <div className="ml-auto">
                                            {bulkSelectedPlants.includes(plant.id) ? (
                                                <div className="bg-plantlix-500 text-white p-1 rounded-full shadow-lg shadow-plantlix-500/20">
                                                    <CheckCircle className="w-4 h-4" />
                                                </div>
                                            ) : (
                                                <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-full text-gray-300">
                                                    <Plus className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                             </div>
                        </div>

                        <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex flex-col">
                                <span className="text-sm font-black text-gray-900 dark:text-white uppercase tracking-widest">
                                    {bulkSelectedPlants.length} ausgewählt
                                </span>
                                <span className="text-[10px] text-gray-500 font-medium">Aus der gesamten Bibliothek</span>
                            </div>
                            <div className="flex gap-3 w-full sm:w-auto">
                                <button 
                                    onClick={() => {
                                        setShowBulkSelector(false);
                                        setBulkSearchQuery('');
                                    }}
                                    className="px-6 py-3 text-gray-500 font-bold uppercase tracking-widest text-[10px] hover:text-gray-900"
                                >
                                    Abbrechen
                                </button>
                                <button 
                                    onClick={handleBulkCreate}
                                    disabled={bulkSelectedPlants.length === 0 || isCreatingBulk}
                                    className="flex-1 sm:flex-none px-8 py-3 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-xl font-black uppercase tracking-widest text-[10px] shadow-xl shadow-plantlix-600/20 disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isCreatingBulk ? (
                                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                                    ) : <CheckSquare className="w-4 h-4 font-black" />}
                                    <span>Erstellen</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </div>
    );
}
